// app/src/lib/revenue/depositSchedule.js — month-boundary cash attribution.
//
// Sales earned near the end of a month are deposited in the first days of the
// NEXT month, so a naive "sum the deposits dated within the month" view both
// understates the closing month and overstates the opening one. This module
// re-attributes those boundary deposits to the month the sales were actually
// earned, using the deposit schedules the owner confirmed:
//
//   Card processor (Finix) + cash — settles T+2 CALENDAR days, rolled forward to
//   the next banking day. By sale weekday:
//       Mon→Wed  Tue→Thu  Wed→Fri  Thu→Mon  Fri→Mon  Sat→Mon  Sun→Tue
//   So a Monday deposit bundles Thu+Fri+Sat; every other weekday deposit is a
//   single sale day. (Owner-confirmed against the Finix feed 2026-08-04: sales
//   on Thu 7/30, Fri 7/31 and Sat 8/1 all landed Mon 8/3. The earlier model —
//   weekend volume folding onto the preceding Friday's batch, then +2 BANKING
//   days — put Fri and Sat on the Tuesday instead, which is what misallocated a
//   Friday month-end.) The Fri→Tue / Sat→Tue pattern is the HOLIDAY-Monday case:
//   the roll produces it automatically, so it needs no special rule.
//
//   Delivery — two different cadences, owner-confirmed 2026-08-04:
//     • DoorDash pays DAILY on the T+2-BANKING-day schedule, with weekend volume
//       folding onto the preceding Friday's batch:
//           Mon→Wed  Tue→Thu  Wed→Fri  Thu→Mon  Fri→Tue  Sat→Tue  Sun→Tue
//       (This is the rule card was wrongly using. On card, Fri/Sat settle Monday;
//       on DoorDash they settle Tuesday. The two schedules really do differ.)
//     • Uber Eats / Grubhub pay a single WEEKLY lump covering the 7 days ending
//       2 days before the payout — anchored to the PAYOUT DAY, not to Mon–Sun.
//       Uber pays Tuesday (⇒ Mon–Sun); Grubhub pays Friday (⇒ Thu–Wed: the
//       Fri Aug 7 2026 payout covers Thu Jul 30 – Wed Aug 5). The lump is opaque
//       per-day, so it is split by the day-of-week sales mix, else evenly.
//   Cadence is measured per payout from the payments themselves (see
//   detectWeeklyCadence) — never hard-coded per platform, never asked of the user.
//
// Edge case we model: the last day of a month falling on a HOLIDAY Monday
// (e.g. Memorial Day = May 31). Pass that date in `holidays`; it pushes the
// Thu→Mon settlement out to Tuesday, so Thursday joins the tail (4 → 5 days).
// All other holidays are rare at month end and intentionally ignored.

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** UTC day-of-week, 0=Sun..6=Sat, for an ISO 'YYYY-MM-DD'. */
function dow(iso) { return new Date(iso + 'T00:00:00Z').getUTCDay(); }

/** Short weekday name for an ISO date. */
export function weekdayName(iso) { return DAY_NAMES[dow(iso)]; }

/** 'YYYY-MM' month key for an ISO date (lexicographically comparable). */
export function monthOf(iso) { return iso.slice(0, 7); }

function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** ISO Monday of the week (Mon-start) containing `iso`. */
function mondayOf(iso) {
  const off = (dow(iso) + 6) % 7; // Mon=0..Sun=6
  return addDays(iso, -off);
}

function toSet(holidays) {
  if (!holidays) return new Set();
  return holidays instanceof Set ? holidays : new Set(holidays);
}

/** A banking day = weekday that is not a recognized holiday. */
function isBusinessDay(iso, holidaySet) {
  const d = dow(iso);
  return d !== 0 && d !== 6 && !holidaySet.has(iso);
}

/**
 * Roll a deposit date forward to the next banking day. Banks do not post on
 * weekends or Fed holidays, so a feed that dates a payout there is approximating
 * — the money really moved on the next banking day, and it covers exactly the
 * sale days that day's payout covers.
 */
export function nextBankingDay(iso, holidaySet) {
  let p = iso;
  while (!isBusinessDay(p, holidaySet)) p = addDays(p, 1);
  return p;
}

/**
 * Deposit date for card/cash sales made on `saleIso`: 2 CALENDAR days later,
 * rolled forward to the next banking day. `holidays` (ISO strings) are treated
 * as non-banking days, so a holiday inside the roll pushes the whole group out
 * together (holiday Monday ⇒ Thu/Fri/Sat all land Tuesday).
 */
export function cardSettlementDate(saleIso, holidays) {
  const holidaySet = toSet(holidays);
  let p = addDays(saleIso, 2);
  while (!isBusinessDay(p, holidaySet)) p = addDays(p, 1);
  return p;
}

/** Step forward `n` banking days from `iso` (skips weekends + holidays). */
function addBusinessDays(iso, n, holidaySet) {
  let cur = iso;
  let left = n;
  while (left > 0) {
    cur = addDays(cur, 1);
    if (isBusinessDay(cur, holidaySet)) left -= 1;
  }
  return cur;
}

// Weekend DoorDash volume settles on the schedule of the preceding Friday's batch.
function batchDay(iso) {
  const d = dow(iso);
  if (d === 6) return addDays(iso, -1); // Sat → Fri
  if (d === 0) return addDays(iso, -2); // Sun → Fri
  return iso;
}

/**
 * Payout date for DAILY delivery (DoorDash) sales made on `saleIso`: 2 BANKING
 * days after the sale's batch day, weekend volume folding onto the preceding
 * Friday. Thu→Mon, Fri/Sat/Sun→Tue. Note this is NOT the card schedule — card
 * settles Fri and Sat on the Monday (see cardSettlementDate).
 */
export function dailyDeliverySettlementDate(saleIso, holidays) {
  return addBusinessDays(batchDay(saleIso), 2, toSet(holidays));
}

/**
 * Sale day(s) feeding a daily-delivery payout posted on `depositIso` — the exact
 * inverse of dailyDeliverySettlementDate, derived by asking the forward function
 * (same technique as cardDepositSaleDays, so the pair cannot drift).
 * A Tuesday payout bundles Fri+Sat+Sun; a Monday payout is Thursday alone.
 */
export function dailyDeliverySaleDays(depositIso, holidays) {
  const holidaySet = toSet(holidays);
  const anchor = nextBankingDay(depositIso, holidaySet); // see cardDepositSaleDays
  const out = [];
  for (let i = 9; i >= 0; i -= 1) {
    const d = addDays(anchor, -i);
    if (dailyDeliverySettlementDate(d, holidaySet) === anchor) out.push(d);
  }
  return out;
}

/**
 * Attribute a DAILY delivery payout stream (DoorDash) to sales-months using the
 * exact banking-day inverse, holiday-aware. Mirrors attributeCardDepositStream.
 * @param {Array<{date:string, amountCents:number}>} deposits
 * @returns {Record<string, number>} month 'YYYY-MM' → cents (integer)
 */
export function attributeDailyDeliveryStream(deposits) {
  if (!deposits || !deposits.length) return {};
  const sorted = [...deposits].sort((a, b) => a.date.localeCompare(b.date));
  const holidays = bankHolidaySetForRange(sorted[0].date, sorted[sorted.length - 1].date);
  const out = {};
  for (const d of sorted) {
    const split = splitByMonth(dailyDeliverySaleDays(d.date, holidays), d.amountCents || 0);
    for (const [m, c] of Object.entries(split)) out[m] = (out[m] || 0) + c;
  }
  return out;
}

/** Payout date for the Mon–Sun delivery week starting on `weekMondayIso`. */
export function deliveryPayoutDateForWeek(weekMondayIso, holidays) {
  const holidaySet = toSet(holidays);
  let p = addDays(addDays(weekMondayIso, 6), 2); // Sunday + 2 → the following Tuesday
  while (!isBusinessDay(p, holidaySet)) p = addDays(p, 1);
  return p;
}

/** Delivery payout date for the week containing an arbitrary `saleIso`. */
export function deliveryPayoutDate(saleIso, holidays) {
  return deliveryPayoutDateForWeek(mondayOf(saleIso), holidays);
}

/**
 * The "tail": every sale day in the closing month whose card/cash cash lands in
 * the next month — i.e. d ≤ L with settlement(d) > L. This single rule yields
 * every leaf of the decision tree (by the weekday L falls on):
 *   Mon→[Sun,Mon] · Tue→[Mon,Tue] · Wed→[Tue,Wed] · Thu→[Wed,Thu] ·
 *   Fri→[Thu,Fri] · Sat→[Thu,Fri,Sat] · Sun→[Thu..Sun] · holiday Mon→[Thu..Mon].
 * @param {string} lastDayIso last calendar day of the month (YYYY-MM-DD)
 * @returns {string[]} tail sale-dates, ascending
 */
export function cardTailDays(lastDayIso, holidays) {
  const holidaySet = toSet(holidays);
  const m = monthOf(lastDayIso);
  const out = [];
  // A week of look-back is always enough: Thursday is the earliest day that can
  // settle after a month-end (Thu→Mon, or Thu→Tue across a holiday Monday).
  for (let i = 6; i >= 0; i -= 1) {
    const d = addDays(lastDayIso, -i);
    if (monthOf(d) !== m) continue;
    if (cardSettlementDate(d, holidaySet) > lastDayIso) out.push(d);
  }
  return out;
}

/**
 * Describe the decision-tree leaf for a month ending on `lastDayIso`.
 * Delivery is "clean" only when the month ends on a Sunday (a delivery-week
 * boundary); otherwise the straddling week must be approximated.
 */
export function endOfMonthLeaf(lastDayIso, holidays) {
  return {
    lastDay: lastDayIso,
    weekday: weekdayName(lastDayIso),
    holidayMonday: toSet(holidays).has(lastDayIso),
    cardTail: cardTailDays(lastDayIso, holidays),
    deliveryMode: weekdayName(lastDayIso) === 'Sun' ? 'clean' : 'approx',
  };
}

/**
 * Card + cash carry: the closing month's cash that deposits next month. Exact —
 * summed from per-sale-day amounts (POS daily closes).
 * @param {string} lastDayIso
 * @param {Record<string, number>} dailyCents  sale-date → card+cash net cents
 */
export function cardCashCarryCents(lastDayIso, dailyCents, holidays) {
  let sum = 0;
  for (const d of cardTailDays(lastDayIso, holidays)) sum += dailyCents[d] || 0;
  return sum;
}

/**
 * Delivery carry: the closing month's delivery cash that deposits next month.
 * For every weekly lump whose payout lands after `lastDayIso`, take the share
 * of that week belonging to the closing month, pro-rated by gross order value
 * (falling back to day-count when no gross is available). This naturally yields
 * the full lump for a Sunday month-end, a pro-rated slice for Thu/Fri/Sat, and
 * the prior full week + Monday sliver for a Monday month-end.
 * @param {string} lastDayIso
 * @param {{ weekLumps: Record<string, number>, grossByDay?: Record<string, number> }} data
 *   weekLumps keyed by the week's Monday (ISO); grossByDay keyed by sale-date.
 */
export function deliveryCarryCents(lastDayIso, data, holidays) {
  const holidaySet = toSet(holidays);
  const weekLumps = data.weekLumps || {};
  const grossByDay = data.grossByDay || {};
  const m = monthOf(lastDayIso);
  const baseMonday = mondayOf(lastDayIso);
  // The only weeks whose payout can land after a month-end are the week
  // containing L and the immediately prior week (the Monday-end case).
  const candidateMondays = [addDays(baseMonday, -7), baseMonday];

  let carry = 0;
  for (const wm of candidateMondays) {
    const lump = weekLumps[wm] || 0;
    if (!lump) continue;
    if (!(deliveryPayoutDateForWeek(wm, holidaySet) > lastDayIso)) continue;

    let grossInMonth = 0;
    let grossTotal = 0;
    let daysInMonth = 0;
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(wm, i);
      const g = grossByDay[d] || 0;
      grossTotal += g;
      if (monthOf(d) === m) { grossInMonth += g; daysInMonth += 1; }
    }
    if (daysInMonth === 0) continue;
    const ratio = grossTotal > 0 ? grossInMonth / grossTotal : daysInMonth / 7;
    carry += Math.round(lump * ratio);
  }
  return carry;
}

/**
 * Settled cashflow for a month, in cents:
 *   raw deposits dated in M  +  Carry(M)  −  Carry(M−1)
 * `Carry(X)` pulls X's late-month cash back from the next month; subtracting
 * Carry(M−1) removes the previous month's tail that posted in early M.
 */
export function settledNetCents({ rawCents, carryThisMonth, carryPrevMonth }) {
  return (rawCents || 0) + (carryThisMonth || 0) - (carryPrevMonth || 0);
}

// ── Inverse: deposit → the sales-month(s) it was earned in ───────────────────
// For the bank-deposit-dated model. Each deposit is a single lump posted on its
// banking date; we run the settlement schedule backwards to find the sale days
// that fed it, then split a boundary-straddling lump by day-count (per-day gross
// is not recoverable from a bank deposit).

/** Last calendar day (ISO) of a 'YYYY-MM' month. */
export function lastDayOfMonth(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/**
 * The one holiday Monday that can fall on a month-end and shift settlement:
 * Memorial Day, the last Monday of May. Returns [ISO] when `month`'s last day is
 * that Monday, else []. (Labor Day, New Year, Christmas never land on a month-end
 * Monday, so they're intentionally not modeled.)
 */
export function monthEndHolidays(month) {
  const last = lastDayOfMonth(month);
  if (last.slice(5) === '05-31' && weekdayName(last) === 'Mon') return [last];
  return [];
}

// ── US bank (Federal Reserve) holiday calendar ──────────────────────────────
// The settlement inverse steps back over BANKING days, so holidays that fall in
// a deposit's T+2 path shift the batch day — which changes month attribution
// whenever the shifted day crosses a month boundary (New Year: a Jan 5 Monday
// deposit ← Dec 31 batch; Labor Day: a Sep 3 Wednesday deposit ← the Aug 29–31
// weekend bundle). Federal Reserve observance: a holiday falling on Sunday is
// observed the following Monday; one falling on Saturday is NOT observed (the
// Fed is open the preceding Friday).

/** ISO date of the nth `dowWanted` (0=Sun..6=Sat) of a month; n<0 counts from the end. */
function nthDowOfMonth(year, month, dowWanted, n) {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month - 1, 1));
    const off = (dowWanted - first.getUTCDay() + 7) % 7;
    return new Date(Date.UTC(year, month - 1, 1 + off + (n - 1) * 7)).toISOString().slice(0, 10);
  }
  const last = new Date(Date.UTC(year, month, 0));
  const off = (last.getUTCDay() - dowWanted + 7) % 7;
  return new Date(Date.UTC(year, month, -off + (n + 1) * 7)).toISOString().slice(0, 10);
}

/** Sunday→Monday observance shift (Saturday is not observed by the Fed). */
function observed(iso) {
  return dow(iso) === 0 ? addDays(iso, 1) : iso;
}

/** Observed US bank holidays for a calendar year, as ISO dates (ascending). */
export function usBankHolidays(year) {
  const fixed = (m, d) => observed(`${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  return [
    fixed(1, 1),                       // New Year's Day
    nthDowOfMonth(year, 1, 1, 3),      // MLK Day — 3rd Monday of January
    nthDowOfMonth(year, 2, 1, 3),      // Washington's Birthday — 3rd Monday of February
    nthDowOfMonth(year, 5, 1, -1),     // Memorial Day — last Monday of May
    fixed(6, 19),                      // Juneteenth
    fixed(7, 4),                       // Independence Day
    nthDowOfMonth(year, 9, 1, 1),      // Labor Day — 1st Monday of September
    nthDowOfMonth(year, 10, 1, 2),     // Columbus Day — 2nd Monday of October
    fixed(11, 11),                     // Veterans Day
    nthDowOfMonth(year, 11, 4, 4),     // Thanksgiving — 4th Thursday of November
    fixed(12, 25),                     // Christmas Day
  ].sort();
}

/** Set of observed bank-holiday ISO dates covering every year touched by [from, to]. */
export function bankHolidaySetForRange(fromIso, toIso) {
  const out = new Set();
  const y0 = Number(fromIso.slice(0, 4));
  const y1 = Number(toIso.slice(0, 4));
  for (let y = y0; y <= y1; y += 1) for (const h of usBankHolidays(y)) out.add(h);
  return out;
}

/**
 * Sale day(s) that feed a card/cash deposit posted on `depositIso` — the exact
 * inverse of cardSettlementDate, obtained by asking the forward function which
 * nearby sale days land on this date. Deriving it this way (rather than
 * re-implementing the rule backwards) makes the two impossible to drift apart.
 * A Monday deposit bundles Thu+Fri+Sat; other weekday deposits are one day.
 * 10 days of look-back covers the longest holiday roll.
 */
export function cardDepositSaleDays(depositIso, holidays) {
  const holidaySet = toSet(holidays);
  // A deposit dated on a weekend or a Fed holiday can't be a real posting date,
  // so no sale day maps to it. Snap it to the next banking day and use THAT
  // day's sale days: the money covers the same trading it would have settled
  // with. (An earlier fallback dumped it on depositIso−2, a single day that the
  // next banking day's payout ALSO claims — so that day was counted twice.)
  const anchor = nextBankingDay(depositIso, holidaySet);
  const out = [];
  for (let i = 9; i >= 0; i -= 1) {
    const d = addDays(anchor, -i);
    if (cardSettlementDate(d, holidaySet) === anchor) out.push(d);
  }
  return out;
}

// ── Weekly delivery: the coverage window is anchored to the PAYOUT day ───────
// A weekly payout covers the 7 days ending WEEKLY_LAG days before it — NOT a
// fixed Mon–Sun week. Owner-confirmed 2026-08-04: Grubhub pays Fri Aug 7 for
// Thu Jul 30 – Wed Aug 5. For a Tuesday payer (Uber) this reproduces Mon–Sun
// exactly, so the old Mon–Sun snap was the Tuesday special case of this rule.
const WEEKLY_LAG = 2;
const WEEKLY_SPAN = 7;

/** Coverage window {from, to} of a weekly delivery payout posted on `depositIso`. */
export function weeklyDeliveryWindow(depositIso) {
  const to = addDays(depositIso, -WEEKLY_LAG);
  return { from: addDays(to, -(WEEKLY_SPAN - 1)), to };
}

/** The 7 sale days a weekly delivery payout covers, ascending. */
export function weeklyDeliverySaleDays(depositIso) {
  const { from } = weeklyDeliveryWindow(depositIso);
  return Array.from({ length: WEEKLY_SPAN }, (_, i) => addDays(from, i));
}

/**
 * Is this payout on the platform's WEEKLY setting? Decided per payment from the
 * gap to that platform's previous payout — never asked of the user, and never
 * hard-coded per platform, so a merchant who switches DoorDash from daily to
 * weekly (or vice versa) is picked up automatically from the next payout on.
 */
export function isWeeklyGap(gapDays) { return gapDays >= 6 && gapDays <= 8; }

/** A gap this short can only come from a platform paying more than once a week. */
const DAILY_GAP_MAX = 4;
/** How many payouts either side of one we look at to judge its cadence. */
const CADENCE_NEIGHBOURHOOD = 6;

/**
 * Per-payout cadence for a channel's payout dates, decided from the payments
 * themselves — never asked of the user, never hard-coded per platform.
 *
 * Judging a payout by its OWN gap alone is too brittle: a daily payer that was
 * closed Fri–Sun shows a 6-day gap and would masquerade as weekly. So a payout
 * counts as weekly only when NO nearby payout of the same channel sits within
 * DAILY_GAP_MAX days — a platform on its daily setting always leaves short gaps
 * somewhere in the neighbourhood. Because the neighbourhood slides, a merchant
 * who switches DoorDash from daily to weekly is picked up a few payouts later
 * with no migration and no prompt.
 * @param {string[]} dates ascending payout dates
 * @returns {boolean[]} weekly? per payout
 */
export function detectWeeklyCadence(dates) {
  const gaps = dates.map((d, i) => (i === 0 ? null : daysBetween(d, dates[i - 1])));
  const fallback = medianGapDays(dates.map((date) => ({ date })));
  return dates.map((_, i) => {
    let minGap = Infinity;
    const lo = Math.max(1, i - CADENCE_NEIGHBOURHOOD);
    const hi = Math.min(dates.length - 1, i + CADENCE_NEIGHBOURHOOD);
    for (let j = lo; j <= hi; j += 1) if (gaps[j] != null) minGap = Math.min(minGap, gaps[j]);
    if (minGap <= DAILY_GAP_MAX) return false;              // something paid again within days → daily
    if (minGap !== Infinity) return isWeeklyGap(minGap) || minGap > 8;
    return isWeeklyGap(fallback);                            // lone payout — no evidence either way
  });
}

/** The Mon–Sun delivery week (by Monday/Sunday ISO) that feeds a payout on `depositIso`. */
export function deliveryDepositWeek(depositIso) {
  let s = addDays(depositIso, -1);
  while (dow(s) !== 0) s = addDays(s, -1); // most recent Sunday strictly before the payout
  return { monday: addDays(s, -6), sunday: s };
}

/** Split `amountCents` across the months of `days` by day-count, summing exactly. */
function splitByMonth(days, amountCents) {
  const counts = new Map();
  for (const d of days) counts.set(monthOf(d), (counts.get(monthOf(d)) || 0) + 1);
  const months = [...counts.keys()];
  const out = {};
  let allocated = 0;
  months.forEach((m, i) => {
    const portion = i === months.length - 1
      ? amountCents - allocated // last month absorbs the rounding remainder
      : Math.round((amountCents * counts.get(m)) / days.length);
    out[m] = portion;
    allocated += portion;
  });
  return out;
}

/** Attribute a card/cash deposit to its sales-month(s): { 'YYYY-MM': cents }. */
export function attributeCardDeposit(depositIso, amountCents, holidays) {
  return splitByMonth(cardDepositSaleDays(depositIso, holidays), amountCents);
}

/** Whole calendar days from `aIso` back to `bIso` (a ≥ b → positive). */
export function daysBetween(aIso, bIso) {
  return Math.round((new Date(aIso + 'T00:00:00Z') - new Date(bIso + 'T00:00:00Z')) / 86400000);
}

/**
 * Median gap (calendar days) between consecutive deposits — the stream's
 * cadence: ~1 for a daily channel, ~7 for a weekly one. 1 for a lone deposit.
 */
export function medianGapDays(deposits) {
  const sorted = [...deposits].sort((a, b) => a.date.localeCompare(b.date));
  const gaps = [];
  for (let i = 1; i < sorted.length; i += 1) gaps.push(daysBetween(sorted[i].date, sorted[i - 1].date));
  if (!gaps.length) return 1;
  return gaps.sort((a, b) => a - b)[Math.floor(gaps.length / 2)];
}

/**
 * Reconcile ONE channel's deposit stream back to the months its sales were
 * earned, by prorating each payout across the days it covers.
 *
 * Each payout settles the orders accrued since that channel's PREVIOUS payout, so
 * the gap between consecutive deposits IS the coverage window — automatically
 * ~1 day for a daily channel (card, DoorDash) and ~7 for a weekly one (Uber Eats
 * on Tuesdays, Grubhub on Fridays), with no per-channel cadence hard-coded. The
 * window is shifted back by `lagDays` (orders settle a couple banking days before
 * the money lands) and the amount is spread evenly across it; each day's slice is
 * credited to its calendar month. A week straddling a month-end therefore splits
 * by day-count, and a daily channel never falsely splits.
 *
 * This supersedes the per-deposit `attributeCard/DeliveryDeposit` pair (which
 * assumed a fixed T+2 card rule and a single weekly Mon–Sun delivery lump); by
 * reading cadence from the deposits themselves it also absorbs holiday shifts and
 * mixed cadences without special cases.
 *
 * @param {Array<{date:string, amountCents:number}>} deposits  one channel's deposits (any order)
 * @param {{ lagDays?:number, maxSpanDays?:number, stats?:{cappedSpans?:number} }} [opts]
 *   lagDays default 2; maxSpanDays caps a deposit's window so a data gap (a paused
 *   channel) can't smear one payout across weeks. Pass `stats` to learn how many
 *   deposits hit that cap (their coverage is approximate — the gap exceeded the cap).
 * @returns {Record<string, number>} month 'YYYY-MM' → cents (fractional; round on use)
 */
export function prorateDepositStream(deposits, opts = {}) {
  const lag = opts.lagDays ?? 2;
  const maxSpan = opts.maxSpanDays ?? 8;
  const sorted = [...deposits].sort((a, b) => a.date.localeCompare(b.date));
  const median = medianGapDays(sorted);
  const out = {};
  for (let i = 0; i < sorted.length; i += 1) {
    const d = sorted[i];
    const rawGap = i > 0 ? daysBetween(d.date, sorted[i - 1].date) : median;
    if (rawGap > maxSpan && opts.stats) opts.stats.cappedSpans = (opts.stats.cappedSpans || 0) + 1;
    const span = Math.max(1, Math.min(rawGap, maxSpan));
    const windowEnd = addDays(d.date, -lag);
    const perDay = (d.amountCents || 0) / span;
    for (let k = 0; k < span; k += 1) {
      const m = monthOf(addDays(windowEnd, -k));
      out[m] = (out[m] || 0) + perDay;
    }
  }
  return out;
}

/**
 * Attribute a whole CARD/CASH deposit stream to sales-months using the EXACT
 * confirmed schedule (T+2 banking days, weekend volume folding onto the Friday
 * batch → Tuesday deposit), holiday-aware via the Fed calendar. Each deposit is
 * inverted independently, so closed days and data gaps don't smear anything.
 * This is exact where prorateDepositStream's calendar-day lag misplaces the
 * weekend fold at Thu/Fri/Sat month-ends.
 * @param {Array<{date:string, amountCents:number}>} deposits
 * @returns {Record<string, number>} month 'YYYY-MM' → cents (integer)
 */
export function attributeCardDepositStream(deposits) {
  if (!deposits || !deposits.length) return {};
  const sorted = [...deposits].sort((a, b) => a.date.localeCompare(b.date));
  const holidays = bankHolidaySetForRange(sorted[0].date, sorted[sorted.length - 1].date);
  const out = {};
  for (const d of sorted) {
    const split = attributeCardDeposit(d.date, d.amountCents || 0, holidays);
    for (const [m, c] of Object.entries(split)) out[m] = (out[m] || 0) + c;
  }
  return out;
}

/**
 * Attribute a WEEKLY delivery payout stream to sales-months: each payout is
 * snapped to the Mon–Sun week strictly before it. This is anchor-correct for
 * any payout weekday — Uber Eats (Tuesday) and Grubhub (Friday) both pay for
 * the prior Mon–Sun week, so a fixed calendar lag is right for one and 3 days
 * off for the other; the snap is right for both.
 *
 * A straddling week splits by `dowWeights` — the business's day-of-week sales
 * mix as [Mon..Sun] relative weights — when provided (restaurants are
 * Fri/Sat-heavy, so a flat split understates a month ending Fri/Sat); without
 * weights it splits by day-count.
 * @param {Array<{date:string, amountCents:number}>} deposits
 * @param {number[]} [dowWeights] relative weights [Mon..Sun]
 * @returns {Record<string, number>} month 'YYYY-MM' → cents (integer)
 */
export function attributeWeeklyDeliveryStream(deposits, dowWeights) {
  const out = {};
  for (const d of deposits || []) {
    const split = attributeDeliveryDeposit(d.date, d.amountCents || 0, weightsAsGrossByDay(d.date, dowWeights));
    for (const [m, c] of Object.entries(split)) out[m] = (out[m] || 0) + c;
  }
  return out;
}

/** Expand [Mon..Sun] weights into the grossByDay shape for a payout's coverage window. */
function weightsAsGrossByDay(depositIso, dowWeights) {
  if (!dowWeights || dowWeights.length !== 7) return undefined;
  const g = {};
  for (const d of weeklyDeliverySaleDays(depositIso)) g[d] = Math.max(0, dowWeights[dowMonIdx(d)] || 0);
  return g;
}

/**
 * Attribute ONE channel's deposit stream to the months its sales were earned,
 * choosing the most accurate model the data supports:
 *
 *   'card'     — weekday-only stream → exact T+2-banking inverse (weekend fold,
 *                Fed holidays). A stream with weekend-dated deposits is a
 *                7-day next-day processor (e.g. Square instant) the T+2 rule
 *                doesn't fit → cadence proration with a 1-day lag.
 *   'cash'     — a bank run deposits cash collected through the PREVIOUS day
 *                (owner-confirmed: a Jun 1 cash deposit is May's cash), so the
 *                gap window ("sales since the last bank run") ends at
 *                deposit − 1 day.
 *   'delivery' — weekly cadence (median gap ≈ 7, Uber Eats / Grubhub) → Mon–Sun
 *                week snap, split by opts.dowWeights when provided; daily
 *                cadence (DoorDash) → exact banking-day inverse (Thu→Mon,
 *                Fri/Sat/Sun→Tue), falling back to cadence proration only if the
 *                stream has weekend-dated payouts.
 *   other      — cadence proration with the caller's opts.
 *
 * @param {Array<{date:string, amountCents:number}>} deposits
 * @param {'card'|'cash'|'delivery'|string} kind
 * @param {{ lagDays?: number, dowWeights?: number[], stats?: object }} [opts]
 *   lagDays overrides the proration paths; dowWeights ([Mon..Sun] sales mix)
 *   weights a weekly delivery week's straddle split; stats collects proration
 *   cap counts (see prorateDepositStream).
 * @returns {Record<string, number>} month 'YYYY-MM' → cents (may be fractional
 *   on proration paths; round on use)
 */
export function attributeDepositStream(deposits, kind, opts = {}) {
  const byDay = attributeStreamByDay(deposits, kind, opts);
  const out = {};
  for (const [date, cents] of Object.entries(byDay)) {
    const m = monthOf(date);
    out[m] = (out[m] || 0) + cents;
  }
  return out;
}

// ── Day-level attribution ────────────────────────────────────────────────────
// ONE function maps a deposit to the sale days that fed it; month totals are a
// fold of it. That is why a month can never disagree with the days inside it —
// they are literally the same numbers summed at different granularity.

const dowMonIdx = (iso) => (dow(iso) + 6) % 7; // Mon=0..Sun=6

/** Sale days of a plain "gap window" stream: `span` days ending `lag` before the payout. */
function spanSaleDays(depositIso, gapDays, lag, maxSpan = 8) {
  const span = Math.max(1, Math.min(gapDays || 1, maxSpan));
  const end = addDays(depositIso, -lag);
  return Array.from({ length: span }, (_, i) => addDays(end, -(span - 1 - i)));
}

/**
 * Resolve the settlement model for a whole stream. Weekend-dated payouts mean the
 * processor is not on a banking-day schedule at all (e.g. a next-day 7/7
 * processor), so those streams fall back to a plain gap window.
 * @returns {'card'|'delivery'|'span1'|'span2'}
 */
function effectiveKind(deposits, kind) {
  const weekendDated = deposits.some((d) => { const w = dow(d.date); return w === 0 || w === 6; });
  if (kind === 'card') return weekendDated ? 'span1' : 'card';
  if (kind === 'cash') return 'span1'; // a bank run banks the cash collected through yesterday
  if (kind === 'delivery') return weekendDated ? 'span2' : 'delivery';
  return 'span2';
}

/**
 * The sale day(s) that fed ONE deposit.
 * @param {string} depositIso
 * @param {'card'|'delivery'|'span1'|'span2'} effKind  from effectiveKind()
 * @param {{ holidays?: Set<string>, gapDays?: number, maxSpanDays?: number }} ctx
 *   gapDays = calendar days since that channel's PREVIOUS payout (span models);
 *   weekly = this payout's detected cadence (see detectWeeklyCadence).
 * @returns {string[]} ascending sale dates
 */
export function depositSaleDays(depositIso, effKind, ctx = {}) {
  if (effKind === 'card') return cardDepositSaleDays(depositIso, ctx.holidays);
  if (effKind === 'delivery') {
    return ctx.weekly
      ? weeklyDeliverySaleDays(depositIso)
      : dailyDeliverySaleDays(depositIso, ctx.holidays);
  }
  return spanSaleDays(depositIso, ctx.gapDays, effKind === 'span1' ? 1 : 2, ctx.maxSpanDays);
}

/**
 * Split `amountCents` across `days`, weighted by the day-of-week sales mix when
 * one is known (restaurants are Fri/Sat-heavy, so an even split understates the
 * weekend inside a bundled payout). Largest-remainder, so the parts sum EXACTLY
 * to the deposit — no cent is created or lost, in either sign.
 * @param {string[]} days
 * @param {number} amountCents  may be negative (a clawback-only day)
 * @param {number[]} [dowWeights] relative weights [Mon..Sun]
 * @returns {Array<{date:string, cents:number}>}
 */
export function splitAcrossDays(days, amountCents, dowWeights) {
  if (!days || !days.length) return [];
  const sign = amountCents < 0 ? -1 : 1;
  const amount = Math.abs(amountCents || 0);
  const weighted = dowWeights && dowWeights.length === 7;
  let w = days.map((d) => (weighted ? Math.max(0, dowWeights[dowMonIdx(d)] || 0) : 1));
  // A ZERO weight removes a day from the payout entirely, and the mix is learned
  // from hand-entered closes — where a zero means "no close was typed for that
  // weekday", NOT "no sales that day". A restaurant whose closes only cover
  // Mon–Fri would otherwise have every weekend bundle collapsed onto the Friday:
  // Sat and Sun read $0, and at a Fri month-end the whole weekend jumps months.
  // So the mix is trusted only when every day this payout covers has evidence;
  // otherwise the payout splits evenly. (Cost: a genuinely-closed day inside a
  // bundle gets a share it didn't earn — small, and confined to that payout,
  // where zeroing a real sales day is neither.)
  if (weighted && w.some((x) => x <= 0)) w = days.map(() => 1);
  let total = w.reduce((a, b) => a + b, 0);
  if (total <= 0) { w = days.map(() => 1); total = days.length; } // all-zero weights → even
  const exact = w.map((x) => (amount * x) / total);
  const out = exact.map((v) => Math.floor(v));
  let rem = amount - out.reduce((a, b) => a + b, 0);
  const order = exact.map((v, i) => [v - out[i], i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; rem > 0; k = (k + 1) % order.length) { out[order[k][1]] += 1; rem -= 1; }
  return days.map((d, i) => ({ date: d, cents: sign * out[i] }));
}

/**
 * Attribute ONE channel's deposit stream to the SALE DAYS it was earned on.
 * Cadence is measured per payment from the gap to that channel's previous
 * payout, so weekly-vs-daily is detected automatically and can change mid-history.
 * @param {Array<{date:string, amountCents:number}>} deposits
 * @param {'card'|'cash'|'delivery'|string} kind
 * @param {{ dowWeights?: number[], maxSpanDays?: number, holidays?: Set<string>, stats?: object }} [opts]
 * @returns {Record<string, number>} sale date 'YYYY-MM-DD' → cents (integer)
 */
export function attributeStreamByDay(deposits, kind, opts = {}) {
  if (!deposits || !deposits.length) return {};
  const sorted = [...deposits].sort((a, b) => a.date.localeCompare(b.date));
  const holidays = opts.holidays
    || bankHolidaySetForRange(sorted[0].date, sorted[sorted.length - 1].date);
  const effKind = effectiveKind(sorted, kind);
  const maxSpan = opts.maxSpanDays ?? 8;
  const median = medianGapDays(sorted);
  const weeklyAt = detectWeeklyCadence(sorted.map((d) => d.date));
  const out = {};
  for (let i = 0; i < sorted.length; i += 1) {
    const d = sorted[i];
    // The first payout has no predecessor — use the stream's own cadence for it.
    const gapDays = i > 0 ? daysBetween(d.date, sorted[i - 1].date) : median;
    if (gapDays > maxSpan && opts.stats) opts.stats.cappedSpans = (opts.stats.cappedSpans || 0) + 1;
    const days = depositSaleDays(d.date, effKind, { holidays, gapDays, maxSpanDays: maxSpan, weekly: weeklyAt[i] });
    for (const { date, cents } of splitAcrossDays(days, d.amountCents || 0, opts.dowWeights)) {
      out[date] = (out[date] || 0) + cents;
    }
  }
  return out;
}

/**
 * Attribute a WEEKLY delivery payout to its sales-month(s): { 'YYYY-MM': cents }.
 * Day-count split of the payout's 7-day coverage window (the days ending 2 before
 * the payout — Mon–Sun for a Tuesday payer, Thu–Wed for a Friday payer). Pass
 * `grossByDay` to weight by gross order value instead.
 */
export function attributeDeliveryDeposit(depositIso, amountCents, grossByDay) {
  const days = weeklyDeliverySaleDays(depositIso);
  if (grossByDay) {
    const counts = new Map();
    let total = 0;
    for (const d of days) { const g = grossByDay[d] || 0; total += g; counts.set(monthOf(d), (counts.get(monthOf(d)) || 0) + g); }
    if (total > 0) {
      const months = [...counts.keys()];
      const out = {};
      let allocated = 0;
      months.forEach((m, i) => {
        const portion = i === months.length - 1 ? amountCents - allocated : Math.round((amountCents * counts.get(m)) / total);
        out[m] = portion; allocated += portion;
      });
      return out;
    }
  }
  return splitByMonth(days, amountCents);
}
